// Turns the events found in a make into a calendar file or Google Calendar links.
// Times are local ("floating"), so each calendar shows them in its own time zone.

const MONTH =
  "(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)";
const DATES = new RegExp(
  [
    `\\b\\d{1,2}(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH}\\b`,
    `\\b${MONTH}\\s+\\d{1,2}(?:st|nd|rd|th)?\\b`,
    "\\b\\d{1,2}[/.]\\d{1,2}[/.]\\d{2,4}\\b",
    "\\b\\d{4}-\\d{2}-\\d{2}\\b",
    "\\b(?:mon|tues|wednes|thurs|fri|satur|sun)day\\b",
    "\\bday \\d{1,2}\\b",
  ].join("|"),
  "i"
);

// Whether a make mentions dates worth offering to put in a calendar
export function mentionsDates(text = "") {
  return DATES.test(text);
}

const compact = (value) => value.replace(/[-:]/g, "");

function nextDay(date) {
  const day = new Date(`${date}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() + 1);
  return day.toISOString().slice(0, 10);
}

function inAnHour(dateTime) {
  const [date, time] = dateTime.split("T");
  const [hours, minutes] = time.split(":").map(Number);
  const moved = new Date(Date.UTC(2000, 0, 1, hours + 1, minutes));
  // Past midnight moves to the next day
  const day = moved.getUTCDate() === 2 ? nextDay(date) : date;
  return `${day}T${moved.toISOString().slice(11, 16)}`;
}

// An end for every event: all-day events end the next day, timed ones an hour later
export function eventEnd(event) {
  if (event.allDay) {
    return event.end ? nextDay(event.end) : nextDay(event.start);
  }
  return event.end || inAnHour(event.start);
}

function icsText(value = "") {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

// Lines longer than 75 characters continue on the next line after a space
function fold(line) {
  const parts = [];
  for (let i = 0; i < line.length; i += 73) {
    parts.push((i ? " " : "") + line.slice(i, i + 73));
  }
  return parts.join("\r\n");
}

function when(name, value, allDay) {
  return allDay
    ? `${name};VALUE=DATE:${compact(value)}`
    : `${name}:${compact(value)}00`;
}

export function buildIcs(events, now = new Date()) {
  const stamp = `${now.toISOString().slice(0, 19).replace(/[-:]/g, "")}Z`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//MakeMeA//Make calendar//EN",
    "CALSCALE:GREGORIAN",
  ];
  events.forEach((event, index) => {
    lines.push(
      "BEGIN:VEVENT",
      `UID:${stamp}-${index}-${Math.random()
        .toString(36)
        .slice(2)}@makemea.com`,
      `DTSTAMP:${stamp}`,
      when("DTSTART", event.start, event.allDay),
      when("DTEND", eventEnd(event), event.allDay),
      `SUMMARY:${icsText(event.title)}`,
      ...(event.location ? [`LOCATION:${icsText(event.location)}`] : []),
      ...(event.description
        ? [`DESCRIPTION:${icsText(event.description)}`]
        : []),
      "END:VEVENT"
    );
  });
  lines.push("END:VCALENDAR");
  return lines.map(fold).join("\r\n") + "\r\n";
}

export function googleCalendarLink(event) {
  const format = (value) =>
    event.allDay ? compact(value) : `${compact(value)}00`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${format(event.start)}/${format(eventEnd(event))}`,
  });
  if (event.location) params.set("location", event.location);
  if (event.description) params.set("details", event.description);
  return `https://calendar.google.com/calendar/render?${params}`;
}
